import { useEffect, useRef, useState } from "react";
import { ScanFace } from "lucide-react";
import { usePasskey, useWebAuthnSupport } from "@/hooks/usePasskey";

/**
 * Botão de login com passkey/biometria.
 * - Ativa Conditional UI (autofill) quando o browser suporta.
 * - Se falhar/cancelar, cai silencioso pro fluxo de senha.
 */
export default function PasskeyButton({ email, onSuccess, label = "Entrar com biometria" }) {
  const { supported, platform, conditional } = useWebAuthnSupport();
  const { loginPasskey } = usePasskey();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const abortRef = useRef(null);

  // Conditional UI: escuta o campo email pra oferecer passkey silenciosamente
  useEffect(() => {
    if (!supported || !conditional) return;
    let cancelled = false;
    const controller = new AbortController();
    abortRef.current = controller;
    (async () => {
      try {
        const data = await loginPasskey(null, { useAutofill: true, signal: controller.signal });
        if (!cancelled && data) onSuccess?.(data);
      } catch {
        /* cancelamento silencioso */
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [supported, conditional, loginPasskey, onSuccess]);

  if (!supported || !platform) return null;

  const doLogin = async () => {
    setBusy(true); setError("");
    try {
      const data = await loginPasskey(email || null);
      onSuccess?.(data);
    } catch (e) {
      if (e?.name === "NotAllowedError") setError("Cancelado. Tenta de novo ou entre com a senha.");
      else if (e?.response?.status === 401) setError("Passkey não reconhecida neste aparelho.");
      else setError("Não deu pra entrar por biometria. Tenta a senha.");
    } finally { setBusy(false); }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={doLogin}
        disabled={busy}
        className="w-full h-14 rounded-full border-2 border-[var(--coffee)] text-[var(--coffee)] font-semibold flex items-center justify-center gap-2 transition-[transform,background-color] active:scale-[0.98] hover:bg-[var(--coffee)] hover:text-[var(--cream)] disabled:opacity-60"
        data-testid="passkey-login-button"
      >
        <ScanFace size={20} />
        {busy ? "verificando…" : label}
      </button>
      {error && <p className="text-xs text-[var(--coral)] mt-2 text-center" data-testid="passkey-login-error">{error}</p>}
    </div>
  );
}
